import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { api, calcCoef } from './api/supabase';
import { AdminPage } from './pages/AdminPage';
import { MarketPage } from './pages/MarketPage';
import { CraftPage } from './pages/CraftPage';
import './App.css';

const fmt = n => Number(n || 0).toLocaleString('ru-RU');

// ── Хук сортировки ───────────────────────────────────────────
function useSort(defaultCol, defaultDir = 'asc') {
  const [col, setCol] = useState(defaultCol);
  const [dir, setDir] = useState(defaultDir);
  const toggle = (newCol) => {
    if (newCol === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCol(newCol); setDir('asc'); }
  };
  const sort = (arr, getters) => {
    const getter = getters[col];
    if (!getter) return arr;
    return [...arr].sort((a, b) => {
      const av = getter(a), bv = getter(b);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), 'ru');
      return dir === 'asc' ? cmp : -cmp;
    });
  };
  return { col, dir, toggle, sort };
}

// ── Заголовок колонки с сортировкой ─────────────────────────
function Th({ id, current, dir, onSort, children, style }) {
  const active = id === current;
  return (
    <th onClick={() => onSort(id)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}>
      {children}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.25, fontSize: 10 }}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );
}

function Topbar() {
  return (
    <div className="topbar"><div className="wrap">
      <span>⚔ L2 Harbor · Eternal C1 x1</span>
      <span>
        <a href="https://forum.l2harbor.com" target="_blank" rel="noreferrer">Форум</a>
        &nbsp;·&nbsp;
        <a href="https://l2harbor.com" target="_blank" rel="noreferrer">Сайт</a>
      </span>
    </div></div>
  );
}

function Header() {
  return (
    <header><div className="wrap">
      <div className="site-title">⚔ АРЕНА СТАВОК ⚔</div>
      <div className="site-sub">Командный Турнир 5×5 · Паримутюэль · Best of 5</div>
      <div className="header-line"><span>◆</span></div>
    </div></header>
  );
}

function HomePage() {
  const [tab, setTab] = useState('tournaments');
  const [tournaments, setTournaments] = useState([]);
  const [activeTId, setActiveTId] = useState(null);
  const [D, setD] = useState({ teams: [], matches: [], bets: [] });
  const [loading, setLoading] = useState(true);
  const [betFilter, setBetFilter] = useState('all');
  const [betsVisible] = useState(() => localStorage.getItem('betsVisible') !== 'false');

  // Сортировки для каждой таблицы
  const matchSort  = useSort('status', 'desc'); // done первые
  const betsSort   = useSort('status', 'desc'); // done первые
  const teamsSort  = useSort('pts', 'desc');
  const ratingSort = useSort('profit', 'desc');

  useEffect(() => {
    api.getTournaments().then(list => {
      setTournaments(list);
      if (list.length) setActiveTId(list[0].id);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!activeTId) return;
    setD({ teams: [], matches: [], bets: [] });
    api.getFullTournamentData(activeTId).then(res => setD(res));
  }, [activeTId]);

  const openTournament = (id) => {
    setActiveTId(id);
    setTab('teams');
    setBetFilter('all');
  };

  // Очки Round Robin
  const pts = {};
  D.teams.forEach(t => { pts[t.id] = { w: 0, l: 0, pts: 0 }; });
  D.matches.forEach(m => {
    if (m.status === 'done' && m.winner_id) {
      const loserId = m.winner_id === m.team_a_id ? m.team_b_id : m.team_a_id;
      if (pts[m.winner_id]) { pts[m.winner_id].w++; pts[m.winner_id].pts += 3; }
      if (pts[loserId]) pts[loserId].l++;
    }
  });

  // Нумерация матчей — фиксированная по исходному порядку
  const matchIndex = {};
  D.matches.forEach((m, i) => { matchIndex[m.id] = i + 1; });

  const totalPool = D.bets.reduce((s, b) => s + Number(b.amount), 0);

  // Рейтинг
  const ratingMap = {};
  D.bets.forEach(b => {
    if (!ratingMap[b.nick]) ratingMap[b.nick] = { bets: 0, won: 0, lost: 0, profit: 0, staked: 0 };
    const r = ratingMap[b.nick];
    r.bets++; r.staked += Number(b.amount);
    const m = D.matches.find(x => x.id === b.match_id);
    if (m?.status === 'done') {
      if (m.winner_id === b.team_id) {
        const coef = calcCoef(D.bets, m.id, b.team_id);
        r.won++; r.profit += Math.floor(Number(b.amount) * coef) - Number(b.amount);
      } else { r.lost++; r.profit -= Number(b.amount); }
    }
  });
  const ratingRaw = Object.entries(ratingMap)
    .map(([nick, r]) => ({ nick, ...r, roi: r.staked > 0 ? parseFloat(((r.profit / r.staked) * 100).toFixed(1)) : 0 }));

  // Статус матча в число для сортировки: done=0, live=1, pending=2
  const statusOrder = s => s === 'done' ? 0 : s === 'live' ? 1 : 2;

  // Отсортированные данные
  const sortedTeamsList = teamsSort.sort(D.teams, {
    name:  t => t.name,
    pts:   t => pts[t.id]?.pts || 0,
    w:     t => pts[t.id]?.w || 0,
    l:     t => pts[t.id]?.l || 0,
  });

  const matchesForTable = D.matches.map((m, i) => {
    const pool = D.bets.filter(b => b.match_id === m.id).reduce((s, b) => s + Number(b.amount), 0);
    const winner = m.winner_id ? D.teams.find(t => t.id === m.winner_id) : null;
    return { ...m, _num: matchIndex[m.id], _pool: pool, _winner: winner?.name || '' };
  });
  const sortedMatches = matchSort.sort(matchesForTable, {
    num:     m => m._num,
    team_a:  m => m.teamA?.name || '',
    team_b:  m => m.teamB?.name || '',
    format:  m => m.format || '',
    pool:    m => m._pool,
    status:  m => statusOrder(m.status),
    winner:  m => m._winner,
  });

  // Строки коэффициентов
  const betsRows = D.matches
    .filter(m => betFilter === 'all' || m.id === betFilter)
    .flatMap(m => {
      const mb = D.bets.filter(b => b.match_id === m.id && b.type !== 'champion');
      const tp = mb.reduce((s, b) => s + Number(b.amount), 0);
      return [m.team_a_id, m.team_b_id].map(tid => {
        const team = D.teams.find(t => t.id === tid);
        const teamP = mb.filter(b => b.team_id === tid).reduce((s, b) => s + Number(b.amount), 0);
        const coef = tp > 0 && teamP > 0 ? Math.max(1.01, tp * 0.95 / teamP) : 2.0;
        const isDone = m.status === 'done';
        const statusLabel = isDone ? (m.winner_id === tid ? 'win' : 'lose') : m.status;
        return {
          _num: matchIndex[m.id],
          _matchId: m.id,
          _tid: tid,
          _teamName: team?.name || '—',
          _teamPool: teamP,
          _coef: coef,
          _potential: Math.floor(100000 * coef),
          _status: statusLabel,
          _statusOrder: isDone ? (m.winner_id === tid ? 0 : 1) : 2,
          _matchName: `${m.teamA?.name} vs ${m.teamB?.name}`,
        };
      });
    });
  const sortedBetsRows = betsSort.sort(betsRows, {
    num:       r => r._num,
    team:      r => r._teamName,
    pool:      r => r._teamPool,
    coef:      r => r._coef,
    potential: r => r._potential,
    status:    r => r._statusOrder,
  });

  const sortedRating = ratingSort.sort(ratingRaw, {
    nick:   r => r.nick,
    bets:   r => r.bets,
    won:    r => r.won,
    lost:   r => r.lost,
    profit: r => r.profit,
    roi:    r => r.roi,
  });

  const navItems = [
    ['tournaments','🏆 Турниры'],['teams','⚔ Команды'],['matches','🗡 Матчи'],
    ['bets','💰 Коэффициенты'],
    ...(betsVisible ? [['betlist','📋 Ставки']] : []),
    ['rating','🏅 Рейтинг'],['rules','📜 Правила'],
  ];
  const activeTournament = tournaments.find(t => t.id === activeTId);

  if (loading) return (
    <><Topbar /><Header />
      <div className="loader" style={{position:'relative',zIndex:1}}>
        <div className="spinner"/><div className="loader-text">Загрузка Арены...</div>
      </div>
    </>
  );

  return (
    <><Topbar /><Header />
      <nav><div className="nav-inner">
        {navItems.map(([id, label]) => (
          <button key={id} className={tab===id?'active':''} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div></nav>

      <div className="wrap"><div className="page-layout">
        <div className="main-col">

          {/* ── ТУРНИРЫ ── */}
          <div className={`section${tab==='tournaments'?' active':''}`}>
            <div className="sec-title">🏆 Турниры</div>
            <div className="sec-sub">Нажми на название турнира чтобы открыть</div>
            {tournaments.map(t => {
              const isActive = t.id === activeTId;
              return (
                <div key={t.id} className="tourn-card" style={isActive?{borderColor:'var(--border2)'}:{}}>
                  <div className="tourn-head">
                    <div>
                      <div className="tourn-name"
                        onClick={() => openTournament(t.id)}
                        style={{cursor:'pointer',textDecoration:'underline',textDecorationColor:'rgba(96,165,250,.4)',textUnderlineOffset:3}}>
                        {t.name}
                      </div>
                      <div className="tourn-meta">
                        <span className={`badge ${t.champion?'badge-done':'badge-active'}`}>
                          {t.champion?'✓ Завершён':'◉ Активен'}
                        </span>
                        {t.type && <span className="badge badge-type">{t.type}</span>}
                        {t.date && <span className="badge badge-type">📅 {t.date}</span>}
                      </div>
                    </div>
                    {t.prize && <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontFamily:'Rajdhani,sans-serif',fontSize:12,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1}}>Призовой фонд</div>
                      <div style={{fontFamily:'JetBrains Mono,monospace',fontSize:18,color:'var(--gold)',fontWeight:700}}>{fmt(t.prize)} ₳</div>
                    </div>}
                  </div>
                  {isActive && (
                    <div className="tourn-body">
                      <div className="tourn-stats">
                        <div className="stat-box"><span className="stat-num">{D.teams.length}</span><div className="stat-lbl">Команд</div></div>
                        <div className="stat-box"><span className="stat-num">{D.matches.length}</span><div className="stat-lbl">Матчей</div></div>
                        <div className="stat-box"><span className="stat-num">{D.bets.length}</span><div className="stat-lbl">Ставок</div></div>
                        <div className="stat-box"><span className="stat-num">{fmt(totalPool)}</span><div className="stat-lbl">Пул (₳)</div></div>
                      </div>
                      {t.champion && <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
                        <span style={{fontSize:13,color:'var(--muted)',fontFamily:'Rajdhani,sans-serif'}}>🏆 Чемпион:</span>
                        <span className="winner-tag">{t.champion}</span>
                      </div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── КОМАНДЫ ── */}
          <div className={`section${tab==='teams'?' active':''}`}>
            <div className="sec-title">⚔ Команды</div>
            <div className="sec-sub">{activeTournament?.name} · кликни заголовок для сортировки</div>
            <div className="pane"><table className="data-table">
              <thead><tr>
                <Th id="name" current={teamsSort.col} dir={teamsSort.dir} onSort={teamsSort.toggle}>Команда</Th>
                <Th id="pts"  current={teamsSort.col} dir={teamsSort.dir} onSort={teamsSort.toggle}>Очки</Th>
                <Th id="w"    current={teamsSort.col} dir={teamsSort.dir} onSort={teamsSort.toggle}>Победы</Th>
                <Th id="l"    current={teamsSort.col} dir={teamsSort.dir} onSort={teamsSort.toggle}>Поражения</Th>
                <Th id="name" current={teamsSort.col} dir={teamsSort.dir} onSort={teamsSort.toggle} style={{width:'40%'}}>Состав</Th>
              </tr></thead>
              <tbody>
                {sortedTeamsList.map(t => {
                  const p = pts[t.id] || {w:0,l:0,pts:0};
                  const players = Array.isArray(t.players)
                    ? t.players
                    : (t.players ? String(t.players).split(',').map(s=>s.trim()) : []);
                  return (
                    <tr key={t.id}>
                      <td className="mteam">{t.name}</td>
                      <td><span className="team-pts">{p.pts} pts</span></td>
                      <td className="txt-win">{p.w}</td>
                      <td className="txt-lose">{p.l}</td>
                      <td><div className="player-list">{players.map((pl,i)=><span key={i} className="player-tag">{pl}</span>)}</div></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>

          {/* ── МАТЧИ ── */}
          <div className={`section${tab==='matches'?' active':''}`}>
            <div className="sec-title">🗡 Сетка матчей</div>
            <div className="sec-sub">{activeTournament?.name} · Round Robin · Best of 5</div>
            <div className="pane"><table className="data-table">
              <thead><tr>
                <Th id="num"    current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>№</Th>
                <Th id="team_a" current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Команда A</Th>
                <Th id="team_b" current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Команда B</Th>
                <Th id="format" current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Формат</Th>
                <Th id="pool"   current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Пул</Th>
                <Th id="status" current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Статус</Th>
                <Th id="winner" current={matchSort.col} dir={matchSort.dir} onSort={matchSort.toggle}>Победитель</Th>
              </tr></thead>
              <tbody>
                {sortedMatches.map(m => (
                  <tr key={m.id}>
                    <td className="mid">М-{String(m._num).padStart(2,'0')}</td>
                    <td className="mteam">{m.teamA?.name||'—'}</td>
                    <td className="mteam">{m.teamB?.name||'—'}</td>
                    <td className="mid">{m.format||'Bo5'}</td>
                    <td className="adena">{fmt(m._pool)} ₳</td>
                    <td>
                      {m.status==='live'?<><span className="dot dot-live"/>LIVE</>:
                       m.status==='done'?<><span className="dot dot-done"/><span className="badge badge-done">Done</span></>:
                       <><span className="dot dot-wait"/><span className="txt-pend">Ожидание</span></>}
                      {m.score&&<span className="mid" style={{marginLeft:6}}>{m.score}</span>}
                    </td>
                    <td>{m._winner?<span className="winner-tag">🏆 {m._winner}</span>:<span className="txt-pend">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          {/* ── КОЭФФИЦИЕНТЫ ── */}
          <div className={`section${tab==='bets'?' active':''}`}>
            <div className="sec-title">💰 Коэффициенты</div>
            <div className="sec-sub">Паримутюэль · маржа 5% · минимум 100,000 адена</div>

            {/* — Чемпион — */}
            {(() => {
              const champBets = D.bets.filter(b => b.type==='champion');
              const champTotal = champBets.reduce((s,b)=>s+Number(b.amount),0);
              const byTeam = {};
              champBets.forEach(b => { byTeam[b.team_id]=(byTeam[b.team_id]||0)+Number(b.amount); });
              const champRows = D.teams.map(t => {
                const pool = byTeam[t.id] || 0;
                const cnt  = champBets.filter(b=>b.team_id===t.id).length;
                const coef = pool>0 ? Math.max(1.01, champTotal*0.95/pool) : null;
                return { team:t.name, pool, cnt, coef };
              }).sort((a,b) => (a.coef||999)-(b.coef||999));
              return (
                <>
                  <div style={{fontFamily:'Rajdhani,sans-serif',fontSize:13,fontWeight:700,color:'var(--blue3)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
                    🏆 На чемпиона
                    <span style={{fontSize:11,color:'var(--muted)',fontWeight:400}}>Общий пул: {fmt(champTotal)} ₳</span>
                  </div>
                  <div className="pane" style={{marginBottom:20}}>
                    <table className="data-table">
                      <thead><tr><th>Команда</th><th>Ставок</th><th>Пул</th><th>Коэффициент</th><th>Потенциал ×100k</th></tr></thead>
                      <tbody>
                        {champRows.map(r => (
                          <tr key={r.team}>
                            <td className="mteam">{r.team}</td>
                            <td className="mid">{r.cnt}</td>
                            <td className="adena">{r.pool>0?fmt(r.pool)+' ₳':'—'}</td>
                            <td className="coef-val">{r.coef?'×'+r.coef.toFixed(2):<span className="txt-pend">—</span>}</td>
                            <td className="adena">{r.coef?fmt(Math.floor(100000*r.coef))+' ₳':'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}

            {/* — На матчи — */}
            {activeTournament?.show_match_bets && (<>
            <div style={{fontFamily:'Rajdhani,sans-serif',fontSize:13,fontWeight:700,color:'var(--blue3)',letterSpacing:1,textTransform:'uppercase',marginBottom:8,display:'flex',alignItems:'center',gap:12}}>
              ⚔ На матчи
            </div>
            <div className="filter-row">
              <label>Матч:</label>
              <select value={betFilter} onChange={e=>{setBetFilter(e.target.value);}}>
                <option value="all">Все</option>
                {D.matches.map((m,i) => (
                  <option key={m.id} value={m.id}>М-{String(i+1).padStart(2,'0')} · {m.teamA?.name} vs {m.teamB?.name}</option>
                ))}
              </select>
            </div>
            <div className="pane"><table className="data-table">
              <thead><tr>
                <Th id="num"       current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Матч</Th>
                <Th id="team"      current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Команда</Th>
                <Th id="pool"      current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Пул на команду</Th>
                <Th id="coef"      current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Коэффициент</Th>
                <Th id="potential" current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Потенциал ×100k</Th>
                <Th id="status"    current={betsSort.col} dir={betsSort.dir} onSort={betsSort.toggle}>Статус</Th>
              </tr></thead>
              <tbody>
                {sortedBetsRows.map(r => (
                  <tr key={`${r._matchId}-${r._tid}`}>
                    <td className="mid">М-{String(r._num).padStart(2,'0')}</td>
                    <td className="mteam">{r._teamName}</td>
                    <td className="adena">{fmt(r._teamPool)} ₳</td>
                    <td className="coef-val">×{r._coef.toFixed(2)}</td>
                    <td className="adena">{fmt(r._potential)} ₳</td>
                    <td>
                      {r._status==='win'  ? <span className="txt-win">✓ Победа</span> :
                       r._status==='lose' ? <span className="txt-lose">✗ Поражение</span> :
                       r._status==='live' ? <><span className="dot dot-live"/>LIVE</> :
                       <span className="txt-pend">Ожидание</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            </>)}
          </div>

          {/* ── СТАВКИ (скрываемая) ── */}
          {betsVisible && (
          <div className={`section${tab==='betlist'?' active':''}`}>
            <div className="sec-title">📋 Ставки</div>
            <div className="sec-sub">Все принятые ставки · коэффициент на момент расчёта</div>

            {/* — На чемпиона — */}
            {(() => {
              const champBets = D.bets.filter(b => b.type==='champion');
              const champTotal = champBets.reduce((s,b)=>s+Number(b.amount),0);
              const champWinner = activeTournament?.champion;

              return (
                <>
                  <div style={{fontFamily:'Rajdhani,sans-serif',fontSize:13,fontWeight:700,color:'var(--blue3)',letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>
                    🏆 Ставки на чемпиона
                  </div>
                  <div className="pane" style={{marginBottom:20}}>
                    <table className="data-table">
                      <thead><tr><th>Ник</th><th>Поставил на</th><th>Сумма</th><th>Коэффициент</th><th>Потенциальный выигрыш</th><th>Итог</th></tr></thead>
                      <tbody>
                        {champBets.length===0 && <tr><td colSpan={6} style={{color:'var(--muted)',textAlign:'center',fontStyle:'italic'}}>Нет ставок</td></tr>}
                        {champBets.map((b,i) => {
                          const team = D.teams.find(t=>t.id===b.team_id);
                          const byTeam = {};
                          champBets.forEach(x => { byTeam[x.team_id]=(byTeam[x.team_id]||0)+Number(x.amount); });
                          const tp = byTeam[b.team_id]||0;
                          const coef = tp>0 ? Math.max(1.01, champTotal*0.95/tp) : 2.0;
                          const potential = Math.floor(Number(b.amount)*coef);
                          const isWin = champWinner && team?.name===champWinner;
                          const isDone = !!champWinner;
                          return (
                            <tr key={b.id||i}>
                              <td className="sb-nick">{b.nick}</td>
                              <td className="mteam">{team?.name||'—'}</td>
                              <td className="adena">{fmt(b.amount)} ₳</td>
                              <td className="coef-val">×{coef.toFixed(2)}</td>
                              <td className="adena">{fmt(potential)} ₳</td>
                              <td>{isDone?(isWin?<span className="txt-win">+{fmt(potential-Number(b.amount))} ₳</span>:<span className="txt-lose">−{fmt(b.amount)} ₳</span>):<span className="txt-pend">—</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}

            {/* — На матчи — */}
            <div style={{fontFamily:'Rajdhani,sans-serif',fontSize:13,fontWeight:700,color:'var(--blue3)',letterSpacing:1,textTransform:'uppercase',marginBottom:8}}>
              ⚔ Ставки на матчи
            </div>
            <div className="pane">
              <table className="data-table">
                <thead><tr><th>Ник</th><th>Матч</th><th>Поставил на</th><th>Сумма</th><th>Коэффициент</th><th>Потенциальный выигрыш</th><th>Итог</th></tr></thead>
                <tbody>
                  {D.bets.filter(b=>b.type!=='champion').length===0 && <tr><td colSpan={7} style={{color:'var(--muted)',textAlign:'center',fontStyle:'italic'}}>Нет ставок</td></tr>}
                  {D.bets.filter(b=>b.type!=='champion').map((b,i) => {
                    const m = D.matches.find(x=>x.id===b.match_id);
                    const team = D.teams.find(t=>t.id===b.team_id);
                    const mb = D.bets.filter(x=>x.match_id===b.match_id && x.type!=='champion');
                    const tp = mb.reduce((s,x)=>s+Number(x.amount),0);
                    const teamP = mb.filter(x=>x.team_id===b.team_id).reduce((s,x)=>s+Number(x.amount),0);
                    const coef = tp>0&&teamP>0 ? Math.max(1.01, tp*0.95/teamP) : 2.0;
                    const potential = Math.floor(Number(b.amount)*coef);
                    const isDone = m?.status==='done';
                    const isWin = isDone && m?.winner_id===b.team_id;
                    const mi = D.matches.findIndex(x=>x.id===b.match_id);
                    return (
                      <tr key={b.id||i}>
                        <td className="sb-nick">{b.nick}</td>
                        <td className="mid">{m?`М-${String(mi+1).padStart(2,'0')} · ${m.teamA?.name} vs ${m.teamB?.name}`:'—'}</td>
                        <td className="mteam">{team?.name||'—'}</td>
                        <td className="adena">{fmt(b.amount)} ₳</td>
                        <td className="coef-val">×{coef.toFixed(2)}</td>
                        <td className="adena">{fmt(potential)} ₳</td>
                        <td>{isDone?(isWin?<span className="txt-win">+{fmt(potential-Number(b.amount))} ₳</span>:<span className="txt-lose">−{fmt(b.amount)} ₳</span>):<span className="txt-pend">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* ── РЕЙТИНГ ── */}
          <div className={`section${tab==='rating'?' active':''}`}>
            <div className="sec-title">🏅 Рейтинг ставочников</div>
            <div className="sec-sub">Накопительная статистика · все матчи</div>
            <div className="pool-row">
              <div className="pool-box"><span className="pool-num">{fmt(totalPool)}</span><div className="pool-lbl">Общий пул ₳</div></div>
              <div className="pool-box"><span className="pool-num">{D.bets.length}</span><div className="pool-lbl">Всего ставок</div></div>
              <div className="pool-box"><span className="pool-num">{sortedRating.length}</span><div className="pool-lbl">Игроков</div></div>
              <div className="pool-box"><span className="pool-num">{D.matches.filter(m=>m.status==='done').length}</span><div className="pool-lbl">Матчей сыграно</div></div>
            </div>
            <div className="pane"><table className="data-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <Th id="nick"   current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>Ник</Th>
                <Th id="bets"   current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>Ставок</Th>
                <Th id="won"    current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>Побед</Th>
                <Th id="lost"   current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>Проигрышей</Th>
                <Th id="profit" current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>Профит (адена)</Th>
                <Th id="roi"    current={ratingSort.col} dir={ratingSort.dir} onSort={ratingSort.toggle}>ROI</Th>
              </tr></thead>
              <tbody>
                {sortedRating.map((r, i) => (
                  <tr key={r.nick} className={i===0?'rank-1':i===1?'rank-2':i===2?'rank-3':''}>
                    <td>{i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}</td>
                    <td className="sb-nick">{r.nick}</td>
                    <td>{r.bets}</td>
                    <td className="txt-win">{r.won}</td>
                    <td className="txt-lose">{r.lost}</td>
                    <td className={r.profit>0?'roi-pos':r.profit<0?'roi-neg':'roi-zer'}>{r.profit>0?'+':''}{fmt(r.profit)} ₳</td>
                    <td className={r.roi>0?'roi-pos':r.roi<0?'roi-neg':'roi-zer'}>{r.roi.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>

          {/* ── ПРАВИЛА ── */}
          <div className={`section${tab==='rules'?' active':''}`}>
            <div className="sec-title">📜 Правила ставок</div>
            <div className="sec-sub">L2 Harbor Tournament · Официальные правила</div>
            <div className="rules-grid">
              <div className="rule-pane"><div className="rule-head">📌 Основные правила</div>
                <div className="rule-item"><span><strong>Минимальная ставка:</strong> 100,000 адена</span></div>
                <div className="rule-item"><span><strong>Максимальная ставка:</strong> 1,000,000 адена</span></div>
                <div className="rule-item"><span><strong>Маржа:</strong> 5% от пула каждого матча</span></div>
                <div className="rule-item"><span><strong>Система:</strong> Паримутюэль — чем меньше ставят на команду, тем выше коэф. Минимум ×1.01</span></div>
                <div className="rule-item"><span><strong>Выигрыш</strong> = Ставка × Коэффициент</span></div>
              </div>
              <div className="rule-pane"><div className="rule-head">👤 Приём ставок</div>
                <div className="rule-item"><span><strong>Принимает:</strong> персонаж <strong>Mysti</strong> · город <strong>Giran</strong></span></div>
                <div className="rule-item"><span><strong>Время онлайн:</strong> 19:00–20:00 (Киев) · 20:00–21:00 (МСК)</span></div>
                <div className="rule-item"><span><strong>Не онлайн?</strong> Пиши в ЛС на форуме — ставку запишем при следующем появлении</span></div>
              </div>
              <div className="rule-pane"><div className="rule-head">⏱ Время приёма ставок</div>
                <div className="rule-item"><span><strong>Дедлайн:</strong> за 10 минут до начала боя</span></div>
                <div className="rule-item"><span>После закрытия ставки не принимаются</span></div>
                <div className="rule-item"><span><strong>На чемпиона</strong> — до начала финала</span></div>
                <div className="rule-item"><span><strong>Скриншот</strong> за 5–10 мин до старта — официальное подтверждение</span></div>
              </div>
              <div className="rule-pane"><div className="rule-head">💸 Выплата выигрышей</div>
                <div className="rule-item"><span><strong>Срок:</strong> при первом появлении онлайн, но не позднее 2–3 дней после матча</span></div>
                <div className="rule-item"><span><strong>Способ:</strong> трейд лично в игре</span></div>
                <div className="rule-item"><span><strong>Банк Гирана:</strong> возможна отправка через банк — учитывай комиссию <strong>−1,000 адена</strong></span></div>
              </div>
              <div className="rule-pane"><div className="rule-head">🔄 Особые случаи</div>
                <div className="rule-item"><span><strong>Ставки только на одну команду:</strong> матч проводится, всем возврат 100%</span></div>
                <div className="rule-item"><span><strong>Команда снялась:</strong> полный возврат или перенос</span></div>
                <div className="rule-item"><span>Ставки на чемпиона снявшейся команды — возврат или перенос</span></div>
                <div className="rule-item"><span>Сыгранные матчи до снятия остаются в силе</span></div>
              </div>
              <div className="rule-pane"><div className="rule-head">⚖ Прочее</div>
                <div className="rule-item"><span><strong>Техсбой:</strong> организатор решает индивидуально</span></div>
                <div className="rule-item"><span><strong>Поддавки:</strong> ставка аннулируется без возврата</span></div>
                <div className="rule-item"><span>Спорные ситуации — финальное слово за организатором</span></div>
              </div>
            </div>
            <div className="pane" style={{marginTop:14}}>
              <div className="pane-head">💬 Как сделать ставку (в игре)</div>
              <div className="pane-body" style={{padding:'16px 18px'}}>
                <div className="step"><div className="step-n">1</div><div className="step-t"><strong>Найди Mysti в Giran</strong> в 19:00–20:00 (Киев) / 20:00–21:00 (МСК) — или напиши в ЛС на форуме.</div></div>
                <div className="step"><div className="step-n">2</div><div className="step-t"><strong>Данные ставки</strong> — ник · на какую команду · матч (М-01) или «ЧЕМПИОН» · сумма (100k–1kk)</div></div>
                <div className="step"><div className="step-n">3</div><div className="step-t"><strong>Трейд</strong> — передай точную сумму. Ставка принята только после получения адены.</div></div>
                <div className="step"><div className="step-n">4</div><div className="step-t"><strong>Проверка</strong> — коэф и потенц. выигрыш появятся в разделе 💰 Коэффициенты.</div></div>
                <div className="step"><div className="step-n">5</div><div className="step-t"><strong>Выплата</strong> — при первом появлении онлайн после матча, не позднее 2–3 дней. Трейд лично или через банк Гирана (−1k).</div></div>
              </div>
            </div>
          </div>

        </div>{/* /main-col */}

        {/* ── SIDEBAR ── */}
        <aside className="sidebar">
          <div className="pane">
            <div className="pane-head">🏆 Турниры</div>
            <div className="pane-body">
              {tournaments.map(t => (
                <div key={t.id} className="sb-item"
                  style={{cursor:'pointer',background:t.id===activeTId?'rgba(59,130,246,.08)':''}}
                  onClick={() => openTournament(t.id)}>
                  <span className="sb-nick" style={{color:t.id===activeTId?'var(--blue2)':'var(--text2)'}}>{t.name}</span>
                  <span className="sb-val">{t.champion?'✓':'◉'}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pane">
            <div className="pane-head">📊 Таблица команд</div>
            <div className="pane-body">
              {[...D.teams].sort((a,b)=>(pts[b.id]?.pts||0)-(pts[a.id]?.pts||0)).map((t,i) => (
                <div key={t.id} className="sb-item">
                  <span style={{color:'var(--muted)',fontFamily:'Rajdhani,sans-serif',fontSize:12,width:16,flexShrink:0}}>{i+1}</span>
                  <span className="sb-nick">{t.name}</span>
                  <span className="sb-val">{pts[t.id]?.pts||0} pts</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pane">
            <div className="pane-head">💰 Топ ставочников</div>
            <div className="pane-body">
              {[...ratingRaw].sort((a,b)=>b.profit-a.profit).slice(0,5).map((r,i) => (
                <div key={r.nick} className="sb-item">
                  <span style={{color:'var(--muted)',fontFamily:'Rajdhani,sans-serif',fontSize:12,flexShrink:0}}>{i+1}</span>
                  <span className="sb-nick">{r.nick}</span>
                  <span className={`sb-val ${r.profit>0?'roi-pos':r.profit<0?'roi-neg':'roi-zer'}`}>{r.profit>0?'+':''}{fmt(r.profit)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="pane">
            <div className="pane-head">📈 Статистика</div>
            <div className="pane-body">
              <div className="sb-item"><span style={{color:'var(--text2)'}}>Матчей</span><span className="sb-val">{D.matches.length}</span></div>
              <div className="sb-item"><span style={{color:'var(--text2)'}}>Сыграно</span><span className="sb-val">{D.matches.filter(m=>m.status==='done').length}</span></div>
              <div className="sb-item"><span style={{color:'var(--text2)'}}>Ставок</span><span className="sb-val">{D.bets.length}</span></div>
              <div className="sb-item"><span style={{color:'var(--text2)'}}>Пул</span><span className="sb-val">{fmt(totalPool)} ₳</span></div>
            </div>
          </div>
        </aside>

      </div></div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/market_online" element={<MarketPage />} />
        <Route path="/craft" element={<CraftPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function NotFoundPage() {
  const [clicks, setClicks] = React.useState(0);
  const [showAdmin, setShowAdmin] = React.useState(false);

  const handleClick = () => {
    const next = clicks + 1;
    setClicks(next);
    if (next >= 3) { setShowAdmin(true); setClicks(0); }
  };

  if (showAdmin) return <AdminPage />;

  return (
    <div style={{ background:'var(--bg)', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, position:'relative', zIndex:1 }}>
      <div
        onClick={handleClick}
        style={{ fontFamily:'Rajdhani,sans-serif', fontSize:'clamp(80px,20vw,180px)', fontWeight:700, color:'var(--border)', letterSpacing:8, cursor:'default', userSelect:'none', lineHeight:1, transition:'color .15s', textShadow:'0 0 60px rgba(59,130,246,.08)' }}
      >
        404
      </div>
      <div style={{ fontFamily:'Rajdhani,sans-serif', fontSize:16, color:'var(--muted)', letterSpacing:3, textTransform:'uppercase' }}>Страница не найдена</div>
      <a href="/" style={{ marginTop:8, fontFamily:'Rajdhani,sans-serif', fontSize:13, color:'var(--blue2)', textDecoration:'none', letterSpacing:1, border:'1px solid rgba(59,130,246,.3)', padding:'8px 20px', borderRadius:3 }}>← На главную</a>
      {clicks > 0 && <div style={{ position:'absolute', bottom:20, fontSize:11, fontFamily:'JetBrains Mono,monospace', color:'var(--border)' }}>{clicks}/3</div>}
    </div>
  );
}

export default App;
